import {
  Container,
  Title,
  Text,
  Button,
  Group,
  Stack,
  Card,
  Badge,
  SimpleGrid,
  Box,
  Paper,
  Grid,
  Divider,
  List,
  ThemeIcon,
} from "@mantine/core";
import {
  IconArrowRight,
  IconTrendingUp,
  IconUsers,
  IconGlobe,
  IconServer,
  IconTelescope,
  IconCode,
  IconChess,
  IconBrandLinkedin,
  IconMail,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";

// Real data from resume
const userProfile = {
  name: "Brian G. Jones",
  title: "Software Engineering Executive & Strategic Leader",
  bio: "I'm a software engineering leader who thrives on bringing order to complexity. I've led large engineering organizations, built reporting systems that cut through noise, and turned scattered workflows into measurable progress. My work is grounded in structure, precision, and the ability to navigate bureaucracy without losing sight of real outcomes. I build trust with teams and stakeholders alike, drive clarity from data, and don't hesitate to challenge the status quo when it stands in the way of results.",
  fullBio:
    "Strategic software engineering executive with 25+ years of progressive technology leadership experience. Currently leading 60+ engineers building comprehensive tourism technology platform.",
  avatar: "/assets/images/design/selfimage.png",
};

const recentProjects = [
  {
    id: "dachshund-app",
    title: "Dachshund Portfolio App",
    description:
      "A production-grade full-stack portfolio application showcasing modern web development practices. Built with React 18, TypeScript, GraphQL, and PostgreSQL, featuring comprehensive backend APIs, real-time data, and enterprise-level infrastructure.",
    slug: "dachshund-app",
    technologyGroups: [
      {
        category: "Frontend",
        technologies: [
          { name: "React 18" },
          { name: "TypeScript" },
          { name: "Mantine UI 8.x" },
          { name: "Apollo Client" },
        ],
      },
      {
        category: "Backend",
        technologies: [
          { name: "Node.js 18+" },
          { name: "GraphQL 16" },
          { name: "PostgreSQL 16" },
          { name: "Prisma" },
        ],
      },
    ],
  },
  {
    id: "eng-insights-platform",
    title: "Engineering Insights Platform",
    description:
      "A comprehensive engineering metrics platform that ingests Jira data to provide actionable insights on sprint commitment, velocity, throughput, and team performance. Features GraphQL API, real-time dashboards, and automated metric derivation.",
    slug: "eng-insights-platform",
    technologyGroups: [
      {
        category: "Frontend",
        technologies: [
          { name: "React 18" },
          { name: "TypeScript" },
          { name: "Mantine UI" },
          { name: "Apollo Client" },
        ],
      },
      {
        category: "Backend",
        technologies: [
          { name: "Node.js" },
          { name: "GraphQL" },
          { name: "PostgreSQL" },
          { name: "Fastify" },
        ],
      },
    ],
  },
];

const stats = [
  {
    label: "Years Experience",
    value: "25+",
    icon: IconTrendingUp,
    color: "#8b42ff",
  },
  {
    label: "Engineering Team Members",
    value: "60+",
    icon: IconUsers,
    color: "#a855f7",
  },
  {
    label: "Global Airlines Served",
    value: "150+",
    icon: IconGlobe,
    color: "#c084fc",
  },
  {
    label: "Market Verticals Led",
    value: "4+",
    icon: IconServer,
    color: "#d8b4fe",
  },
];

const sideQuests = [
  {
    title: "Astrophotography",
    description:
      "Hauling too much kit into the night, capturing galaxies, and swearing at guiding errors. There's something deeply satisfying about spending hours in the cold to capture photons that have traveled millions of years just to hit my sensor.",
    icon: IconTelescope,
    color: "blue",
  },
  {
    title: "Wargame Design",
    description:
      "Creator of Ashes of Empire, a post-WWI block and card game about rot, collapse, and the fine art of losing slowly. Because apparently I enjoy designing systems where everyone suffers equally.",
    icon: IconChess,
    color: "red",
  },
  {
    title: "Tabletop Gaming",
    description:
      "Red Strike, Sons of Drums: Battle of Eylau, Valley of Tears, Traces of War… basically anything with maps, dice, and the chance to replay history's worst days. I have a particular fondness for games that make you think three moves ahead.",
    icon: IconChess,
    color: "green",
  },
  {
    title: "Code Experiments",
    description:
      'From Python ETL pipelines to React dashboards and Dockerized Franken-apps. I build things that solve problems, even if those problems are sometimes "I wonder if I can make this work with three different databases."',
    icon: IconCode,
    color: "violet",
  },
];

const workPhilosophy = [
  {
    title: "Structure Over Chaos",
    description:
      "I believe in systems that make sense, documentation that exists, and code that doesn't require a PhD to understand. Boring is often better.",
  },
  {
    title: "Question Everything",
    description:
      '"We\'ve always done it this way" is not a valid technical argument. I like to poke at assumptions until they either prove themselves or fall apart.',
  },
  {
    title: "Build to Last",
    description:
      "Whether it's code, teams, or telescope mounts, I prefer things that work reliably over things that work spectacularly once.",
  },
];

export function HomePage() {
  try {
    return (
      <Box className="homepage-background" style={{ minHeight: "100vh" }}>
        <Container size="lg" py="md">
          <Stack gap="lg">
            {/* Hero Section with transparent portrait on right */}
            <Box
              style={{
                position: "relative",
                minHeight: "45vh",
                display: "flex",
                alignItems: "center",
                overflow: "hidden",
                borderRadius: "16px",
                backgroundColor: "var(--card-background)",
                border: "none",
              }}
            >
              {/* Transparent portrait positioned to the right */}
              <Box
                style={{
                  position: "absolute",
                  top: "50%",
                  right: "-50px",
                  transform: "translateY(-50%)",
                  width: "400px",
                  height: "500px",
                  backgroundImage: "url(/assets/images/design/selfimage.png)",
                  backgroundSize: "contain",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  opacity: 0.3,
                  zIndex: 1,
                }}
              />

              {/* Content positioned to the left */}
              <Box
                style={{
                  position: "relative",
                  zIndex: 3,
                  width: "100%",
                  padding: "2rem",
                }}
              >
                <Stack
                  gap="md"
                  style={{ maxWidth: "600px", textAlign: "left" }}
                >
                  <div>
                    <Title
                      order={1}
                      size="h1"
                      mb="sm"
                      className="text-gradient"
                      style={{
                        fontSize: "3.2rem",
                        fontWeight: 700,
                        fontFamily: "Noto Sans KR, sans-serif",
                      }}
                    >
                      {userProfile.name}
                    </Title>
                    <Text
                      size="xl"
                      className="text-secondary-dark"
                      mb="md"
                      fw={500}
                    >
                      {userProfile.title}
                    </Text>
                    <Text
                      size="lg"
                      className="text-secondary-dark"
                      lh={1.5}
                      style={{ maxWidth: "500px" }}
                    >
                      {userProfile.bio}
                    </Text>
                  </div>

                  <Group gap="md" mt="lg">
                    <Button
                      component={Link}
                      to="/projects"
                      size="lg"
                      className="btn-gradient hover-lift"
                      leftSection={<IconArrowRight size={20} />}
                    >
                      View My Work
                    </Button>
                    <Button
                      component="a"
                      href="https://www.linkedin.com/in/brian-g-jones/"
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outline"
                      size="lg"
                      className="hover-lift"
                      leftSection={<IconBrandLinkedin size={20} />}
                      style={{
                        borderColor: "var(--purple-500)",
                        color: "var(--purple-400)",
                        backgroundColor: "rgba(139, 66, 255, 0.1)",
                        backdropFilter: "blur(10px)",
                      }}
                    >
                      Connect
                    </Button>
                  </Group>
                </Stack>
              </Box>
            </Box>

            {/* Enhanced Stats Section inspired by Stakent */}
            <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
              {stats.map((stat, index) => {
                const IconComponent = stat.icon;
                return (
                  <Paper
                    key={index}
                    className={`stats-card hover-lift float-animation`}
                    style={{ animationDelay: `${index * 0.2}s` }}
                  >
                    <Stack gap="xs" ta="center">
                      <Box
                        style={{
                          background: `linear-gradient(135deg, ${stat.color}20, ${stat.color}10)`,
                          borderRadius: "12px",
                          padding: "12px",
                          display: "inline-flex",
                          margin: "0 auto",
                        }}
                      >
                        <IconComponent size={24} color={stat.color} />
                      </Box>

                      <Text
                        size="xl"
                        fw={700}
                        className="text-primary-dark"
                        style={{ fontSize: "2rem" }}
                      >
                        {stat.value}
                      </Text>

                      <Text size="sm" className="text-muted-dark" fw={500}>
                        {stat.label}
                      </Text>
                    </Stack>
                  </Paper>
                );
              })}
            </SimpleGrid>

            {/* Enhanced Recent Projects Section */}
            <Stack gap="md">
              <Group justify="space-between" align="center">
                <Title order={2} className="text-primary-dark" fw={600}>
                  Recent Projects
                </Title>
                <Button
                  component={Link}
                  to="/projects"
                  variant="subtle"
                  rightSection={<IconArrowRight size={16} />}
                  className="hover-lift"
                  style={{ color: "var(--purple-400)" }}
                >
                  View All
                </Button>
              </Group>

              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                {recentProjects.slice(0, 2).map((project, index) => (
                  <Paper
                    key={project.id}
                    className="hover-lift"
                    p="lg"
                    component={Link}
                    to={`/projects/${project.slug}`}
                    style={{
                      textDecoration: "none",
                      color: "inherit",
                      animationDelay: `${index * 0.1}s`,
                      backgroundColor: "var(--card-background)",
                      border: "none",
                      borderRadius: "12px",
                    }}
                  >
                    <Stack gap="sm">
                      <Title order={3} size="h4" className="text-primary-dark">
                        {project.title}
                      </Title>
                      <Text className="text-secondary-dark" size="sm" lh={1.5}>
                        {project.description}
                      </Text>
                      <Group gap="xs" mt="sm">
                        {project.technologyGroups
                          ?.flatMap((group: any) =>
                            group.technologies
                              .slice(0, 2)
                              .map((tech: any, techIndex: number) => (
                                <Badge
                                  key={tech.name}
                                  variant="light"
                                  size="sm"
                                  style={{
                                    background: `rgba(139, 66, 255, ${0.1 + techIndex * 0.05})`,
                                    color: "var(--purple-300)",
                                    border: "none",
                                  }}
                                >
                                  {tech.name}
                                </Badge>
                              ))
                          )
                          .slice(0, 4)}
                      </Group>
                    </Stack>
                  </Paper>
                ))}
              </SimpleGrid>
            </Stack>

            {/* Side Quests Section */}
            <Card
              p="xl"
              style={{
                backgroundColor: "var(--card-background)",
                border: "1px solid rgba(139, 92, 246, 0.4)",
              }}
            >
              <Stack gap="lg">
                <Title order={2} style={{ color: "#8b5cf6" }}>
                  Side Quests
                </Title>
                <Grid>
                  {sideQuests.map((quest, index) => (
                    <Grid.Col key={index} span={{ base: 12, md: 6 }}>
                      <Stack gap="md">
                        <Group gap="sm">
                          <ThemeIcon
                            size="lg"
                            variant="light"
                            color={quest.color}
                          >
                            <quest.icon size={20} />
                          </ThemeIcon>
                          <Title order={3} style={{ color: "#ffffff" }}>
                            {quest.title}
                          </Title>
                        </Group>
                        <Text style={{ color: "#d1d5db" }}>
                          {quest.description}
                        </Text>
                      </Stack>
                    </Grid.Col>
                  ))}
                </Grid>
              </Stack>
            </Card>

            {/* How I Work Section */}
            <Card
              p="xl"
              style={{
                backgroundColor: "var(--card-background)",
                border: "1px solid rgba(139, 92, 246, 0.4)",
              }}
            >
              <Stack gap="lg">
                <Title order={2} style={{ color: "#ffffff" }}>
                  How I Work
                </Title>
                <Grid>
                  {workPhilosophy.map((philosophy, index) => (
                    <Grid.Col key={index} span={{ base: 12, md: 4 }}>
                      <Stack gap="sm">
                        <Title order={4} style={{ color: "#8b5cf6" }}>
                          {philosophy.title}
                        </Title>
                        <Text size="sm" style={{ color: "#a1a1aa" }}>
                          {philosophy.description}
                        </Text>
                      </Stack>
                    </Grid.Col>
                  ))}
                </Grid>
              </Stack>
            </Card>

            {/* Portfolio Purpose */}
            <Card
              p="xl"
              style={{
                backgroundColor: "var(--card-background)",
                border: "1px solid rgba(139, 92, 246, 0.4)",
              }}
            >
              <Stack gap="lg">
                <Title order={2} style={{ color: "#ffffff" }}>
                  Portfolio Purpose
                </Title>
                <Text size="lg" style={{ color: "#d1d5db" }}>
                  This site is my personal lab: part resume, part showcase, part
                  sandbox. Expect professional highlights, hobby projects, and
                  the occasional experiment that went sideways but still taught
                  me something useful.
                </Text>

                <Divider />

                <Text size="md" style={{ color: "#a1a1aa" }}>
                  You&apos;ll find a mix of:
                </Text>

                <List spacing="sm" size="md">
                  <List.Item>
                    <Text style={{ color: "#d1d5db" }}>
                      Engineering projects that actually shipped
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text style={{ color: "#d1d5db" }}>
                      Astrophotography that didn&apos;t get ruined by clouds
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text style={{ color: "#d1d5db" }}>
                      Game designs that survived playtesting
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text style={{ color: "#d1d5db" }}>
                      Code experiments that taught me something new
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text style={{ color: "#d1d5db" }}>
                      The occasional rant about why certain technologies exist
                    </Text>
                  </List.Item>
                </List>
              </Stack>
            </Card>

            {/* Contact Section */}
            <Card
              p="xl"
              style={{
                backgroundColor: "var(--card-background)",
                border: "1px solid rgba(139, 66, 255, 0.3)",
                color: "white",
              }}
            >
              <Stack gap="md" align="center">
                <Title order={2} className="text-primary-dark" ta="center">
                  Let&apos;s Connect
                </Title>
                <Group gap="md" justify="center">
                  <Badge
                    size="lg"
                    variant="light"
                    style={{
                      background: "rgba(139, 66, 255, 0.2)",
                      color: "var(--purple-300)",
                      border: "1px solid rgba(139, 66, 255, 0.3)",
                    }}
                  >
                    Available for interesting problems
                  </Badge>
                  <Badge
                    size="lg"
                    variant="light"
                    style={{
                      background: "rgba(139, 66, 255, 0.15)",
                      color: "var(--purple-300)",
                      border: "1px solid rgba(139, 66, 255, 0.3)",
                    }}
                  >
                    Coffee-powered
                  </Badge>
                  <Badge
                    size="lg"
                    variant="light"
                    style={{
                      background: "rgba(139, 66, 255, 0.1)",
                      color: "var(--purple-300)",
                      border: "1px solid rgba(139, 66, 255, 0.3)",
                    }}
                  >
                    Telescope-equipped
                  </Badge>
                </Group>

                <Group gap="md" justify="center" mt="md">
                  <Button
                    className="btn-gradient hover-lift"
                    size="md"
                    leftSection={<IconBrandLinkedin size={18} />}
                    component="a"
                    href="https://www.linkedin.com/in/brian-g-jones/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Connect on LinkedIn
                  </Button>
                  <Button
                    variant="outline"
                    size="md"
                    leftSection={<IconMail size={14} />}
                    component="a"
                    href="/contact"
                    style={{
                      borderColor: "var(--purple-500)",
                      color: "var(--purple-400)",
                      backgroundColor: "rgba(139, 66, 255, 0.1)",
                    }}
                    className="hover-lift"
                  >
                    Contact Me
                  </Button>
                </Group>
              </Stack>
            </Card>
          </Stack>
        </Container>
      </Box>
    );
  } catch (error) {
    console.error("HomePage render error:", error);
    return (
      <Container size="lg" py="xl">
        <Stack gap="md" ta="center">
          <Title order={1}>Welcome</Title>
          <Text>Loading...</Text>
        </Stack>
      </Container>
    );
  }
}
